import React, { Fragment, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button } from 'reactstrap';
import { useEmployeesUIContext } from '../EmployeesUIProvider';
import EmployeesTableColumns, { EmployeesColumns } from './EmployeesTableColumns';
import { EmployeesTableSearchBar } from './EmployeesTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getEmployeeList, setEmployeeFilters } from '../../redux/actions';
import EyeIcon from 'mdi-react/EyeIcon';
import QrCodeIcon from 'mdi-react/QrcodeIcon'
import PencilIcon from 'mdi-react/PencilIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';
import DeleteIcon from 'mdi-react/DeleteIcon';

const EmployeesTable = () => {
  const employeUIContext = useEmployeesUIContext();
  const dispatch = useDispatch();
  const {
    data: employees,
    error,
    filters,
    loading,
    totalRecords,
  } = useSelector((state) => state.cadsAIEmployees, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [columns, setColumns] = useState(0);

  useEffect(() => {
    setColumns(EmployeesColumns.length);
  }, [EmployeesColumns.length]);

  React.useEffect(() => {
    dispatch(getEmployeeList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setEmployeeFilters({
        sortBy: property,
        isSortOrderDescending: !isSortOrderDescending,
      }),
    );
  };

  const renderActionButtons = (slug,employeeId) => (
      <div className="actions">
        <Button
            className="icon mb-0"
            color="success"
            outline
            onClick={() => employeUIContext.openEmployeeQRDialog(slug)}
        >
          <p>
            <QrCodeIcon/> View QR
          </p>
        </Button>
        <Button
            className="icon mb-0"
            color="success"
            outline
            onClick={() => employeUIContext.openViewEmployeesDialog(slug)}
        >
          <p>
            <EyeIcon/> View
          </p>
        </Button>
      <Button
        className="icon mb-0"
        color="primary"
        outline
        onClick={() => employeUIContext.openEditEmployeesDialog(slug, employeeId)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
      <Button
        className="icon mb-0"
        color="danger"
        outline
        onClick={() => employeUIContext.openDeleteEmployeesDialog(employeeId)}
      >
        <p>
          <DeleteIcon /> Remove
        </p>
      </Button>
    </div>
  );

  const handleChangePage = (event, currentPage) => {
    dispatch(setEmployeeFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setEmployeeFilters({pageSize: +event.target.value, pageNumber: 1}));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - employees.length : 0;

  return (
    <Fragment>
      <div className="material-table__wrap">
        <EmployeesTableSearchBar />
        <Table className="material-table">
          <EmployeesTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={columns}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon/>
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading employees...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!employees?.length && !loading && !error && (
                <TableRow style={{height: 120}}>
                  <TableCell colSpan={columns} align="center">
                    {filters.status === null && filters.search === ''
                        ? 'No Employees'
                        : 'No Employees with applied filters'}
                  </TableCell>
                </TableRow>
            )}
            {employees?.map((employee) => {
              return (
                  <TableRow className="material-table" tabIndex={-1} key={employee?.id}>
                    <TableCell className="material-table__cell" align="center">
                      <img className={'module_logo_image'} src={employee.photoUrl} alt={'Employee Image'}/>
                    </TableCell>
                    <TableCell align="center" className="material-table__cell">
                      {employee.firstName}
                    </TableCell>
                    <TableCell align="center" className="material-table__cell">
                      {employee.lastName}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {employee.email}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: 250 }}
                  >
                    {renderActionButtons(employee?.slug, employee?.id)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={columns} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default EmployeesTable;
